<template>
    <div class="mpcStatistics">
        <v-main>
            <v-row class="header-row">
                <v-col class="v-col-md-3 v-col-sm-5">
                    <div class="buttons">
                        <v-btn
                            data-id="ChooseViewButton"
                            variant="text"
                            tile
                            flat
                            :rounded="0"
                            :class="{
                                activeBtn: layout === 'previousDay'
                            }"
                            @click="setDaysBefore(1)"
                        >
                            Dagen før
                        </v-btn>
                        <v-btn
                            data-id="ChooseViewButton"
                            variant="text"
                            tile
                            flat
                            :rounded="0"
                            :class="{
                                activeBtn: layout === 'today'
                            }"
                            @click="setDaysBefore(0)"
                        >
                            I dag
                        </v-btn>
                    </div>
                </v-col>
                <v-col class="v-col-md-2 v-col-sm-4">
                    <datepicker
                        v-model="statisticsDate"
                        class="datepicker"
                        data-id="DatePicker"
                        :max-date="todayDate"
                        format="dd/MM/yyyy"
                        :clearable="false"
                        auto-apply
                        utc
                        no-today
                        :enable-time-picker="false"
                        @update:model-value="setStatisticsDate"
                    />
                    <v-menu
                        v-model="statisticsDateMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <vue-good-table
                        v-if="statisticsStore.mpcStatistics"
                        :columns="columns"
                        :rows="rows"
                        :sort-options="{
                            enabled: false
                        }"
                        :enable-row-expand="true"
                        style-class="vgt-table"
                    >
                        <template #table-row="props">
                            <span v-if="props.column.field == 'rowControls'">
                                <button v-if="props.expandedRow" type="button" data-id="EditIcon" class="edit-icon material-icons">
                                    remove
                                </button>
                                <button v-else type="button" data-id="EditIcon" class="edit-icon material-icons">add</button>
                            </span>
                        </template>
                        <template #row-details="props">
                            <table class="subdata-table" aria-label="MPC statistics">
                                <template
                                    v-for="(departure, i) in props.row.departures"
                                    :key="`${props.row.lineName} + ${departure.departure} + ${i}`"
                                >
                                    <thead>
                                        <th>..</th>
                                        <th>Linje</th>
                                        <th>Inn</th>
                                        <th>Ut</th>
                                        <th>Inn</th>
                                        <th>Ut</th>
                                        <th>Kjøretøy etterlatt</th>
                                        <th>Operatør</th>
                                    </thead>
                                    <tr class="subdata">
                                        <td colspan="8" class="subdata-title">
                                            Avgang:
                                            {{ departure.departure }}
                                            Mål:
                                            {{ departure.destination }}
                                        </td>
                                    </tr>
                                    <template
                                        v-for="(trip, y) in departure.trips"
                                        :key="`${trip.tripId} + ${props.row.lineName} + ${departure.departure} + ${y}`"
                                    >
                                        <tr :class="[isLastTrip(i, props.row.departures.length, y, departure.trips.length)]">
                                            <td />
                                            <td>
                                                {{ time(new Date(trip.departureAt)) }}
                                            </td>
                                            <td>
                                                {{ trip.passengersIn }}
                                            </td>
                                            <td>
                                                {{ trip.passengersOut }}
                                            </td>
                                            <td>
                                                {{ trip.carsIn }}
                                            </td>
                                            <td>
                                                {{ trip.carsOut }}
                                            </td>
                                            <td>
                                                {{ trip.carsLeftBehind }}
                                            </td>
                                            <td>
                                                <span>{{ props.row.companyName }}</span>
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                            </table>
                        </template>
                    </vue-good-table>
                    <v-overlay v-else>
                        <v-progress-circular indeterminate size="64" />
                    </v-overlay>
                </v-col>
            </v-row>
        </v-main>
    </div>
</template>

<script setup lang="ts">
import moment from 'moment';
import { ref, onBeforeUnmount, watch } from 'vue';
import { VueGoodTable } from 'vue-good-table-next';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useStatisticsStore } from '@/features/statistics/statisticsStore';
import { FormatDateISO } from '@/helpers/DateFormatHelpers';
import { useDateTimeFilters } from '@/helpers/useDateTimeFilters';

const layout = ref('today');
const syncTimeout = 60000; // 1 min
let syncInterval = -1;
const statisticsDateMenu = ref(false);
const statisticsDate = ref(new Date().toISOString().substr(0, 10));
const todayDate = new Date().toISOString().substr(0, 10);
const { time } = useDateTimeFilters();

const columns = [
    {
        label: '',
        field: 'rowControls'
    },
    {
        label: 'Linje',
        field: 'lineName'
    },
    {
        label: 'Inn',
        field: 'passengersIn'
    },
    {
        label: 'Ut',
        field: 'passengersOut'
    },
    {
        label: 'Inn',
        field: 'carsIn'
    },
    {
        label: 'Ut',
        field: 'carsOut'
    },
    {
        label: 'Kjøretøy etterlatt',
        field: 'carsLeftBehind'
    },
    {
        label: 'Operatør',
        field: 'companyName'
    }
];
const rows = ref([]);

const statisticsStore = useStatisticsStore();

watch(
    () => statisticsStore.mpcStatistics,
    async stats => {
        if (stats) {
            rows.value = [];
            stats.forEach(item => {
                rows.value.push({ ...item });
            });
        }
    }
);

initialize();

async function initialize() {
    await fetchMpcStatistics();
    syncInterval = window.setInterval(syncMpcStatistics, syncTimeout);
}

onBeforeUnmount(() => {
    if (syncInterval) {
        clearInterval(syncInterval);
        syncInterval = undefined;
    }
});

function isLastTrip(i: number, departuresCount: number, y: number, tripsCount: number) {
    return i === departuresCount - 1 && y === tripsCount - 1 ? 'trips-end' : '';
}

async function syncMpcStatistics() {
    statisticsStore.fetchMpcStatistics(true);
}

async function fetchMpcStatistics() {
    return statisticsStore.fetchMpcStatistics(false);
}

async function setDaysBefore(day: number) {
    const requestedDate = new Date();
    requestedDate.setDate(requestedDate.getDate() - day);

    const formattedDate = FormatDateISO(requestedDate);

    rows.value = [];
    rows.value.push({ lineName: 'test' });

    if (day === 0) {
        layout.value = 'today';
    } else {
        layout.value = 'previousDay';
    }
    statisticsStore.currentDate = formattedDate;
    setLayout();
    await fetchMpcStatistics();
}

async function setStatisticsDate() {
    statisticsStore.currentDate = statisticsDate.value;
    setLayout();
    await fetchMpcStatistics();
}

function setLayout() {
    const today = moment(todayDate);
    const currentDate = moment(statisticsStore.currentDate);
    const diff = today.diff(currentDate, 'days');
    if (diff === 0) {
        layout.value = 'today';
    } else if (diff === 1) {
        layout.value = 'previousDay';
    } else {
        layout.value = '';
    }
    statisticsDate.value = statisticsStore.currentDate;
}
</script>

<style lang="scss" scoped>
.subdata-table {
    width: calc(100% + 24px);
    margin: -12px -12px;

    thead {
        visibility: collapse;

        th {
            border-bottom: none;
        }
    }

    .subdata-title {
        text-align: center;
    }

    span {
        visibility: hidden;
    }
}

.datepicker {
    padding-top: 12px;
}

.buttons {
    padding-top: 14px;
}

.activeBtn {
    border-bottom: 2px solid black;
}

.subdata {
    background-color: rgb(224, 224, 224);
}

table {
    border-collapse: collapse;
    margin: auto;
    width: 100%;
}
</style>

<style lang="scss">
// style for the unscoped table component
.vgt-wrap {
    .vgt-inner-wrap {
        box-shadow: none;

        .vgt-table {
            border: none;
            margin-top: 50px;

            thead th {
                background: transparent;
                border-bottom: 2px solid currentColor;
                color: rgba(0, 0, 0, 0.6);
                position: relative;

                &:nth-child(3) {
                    &::after {
                        content: 'Passasjerer';
                        position: absolute;
                        top: -30px;
                        left: 24px;
                    }
                }

                &:nth-child(5) {
                    &::after {
                        content: 'Kjøretøy';
                        position: absolute;
                        top: -30px;
                        left: 32px;
                    }
                }
            }

            .subdata-table thead {
                border-bottom: thin solid rgba(0, 0, 0, 0.12);

                th {
                    border: none;
                }
            }

            .edit-icon {
                transform: rotate(0.03deg);
                vertical-align: middle;

                &::after {
                    background-color: currentColor;
                    border-radius: 50%;
                    content: '';
                    display: inline-block;
                    height: 100%;
                    left: 0;
                    opacity: 0;
                    pointer-events: none;
                    position: absolute;
                    top: 0;
                    transform: scale(1.3);
                    width: 100%;
                    transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
                }
                &:focus ::after {
                    opacity: 0.12;
                }
            }
        }
    }
}

.datepicker {
    border-bottom: 1px solid rgba(0, 0, 0, 0.6);

    .dp__input {
        border: none;
    }
}
</style>
@/services/emitter
